import type { VisualizationAssetSettings } from "@/shared/domain/urdf";

/**
 * The wizard's orientation control, translated to and from the wire.
 *
 * The request field is `z_up`, and `true` on it means **keep the source's Z-up
 * frame** — the converter uses an identity root matrix when it is set and
 * rotates Z-up into glTF's Y-up when it is not. The control the user sees states
 * the opposite proposition ("orient the asset to Roboto's viewer axis
 * orientation"), so it is `!z_up` and every read and write of it has to invert.
 *
 * That inversion is the whole reason this lives in its own module rather than
 * inline in the checkbox's `checked`: a mis-sent boolean here looks perfectly
 * right on screen and silently converts every robot the wrong way up, so it is
 * worth pinning on both sides in a test that never renders anything.
 */

/**
 * Whether the orientation control reads as on, for settings spelled the wire's
 * way.
 *
 * Accepts a partial block, because a *stored* settings block — what an earlier
 * conversion was run with — is free-form metadata that may predate the field.
 * An absent `z_up` is the backend's default of `false`, i.e. the conversion did
 * rotate, so the control is on.
 */
export function orientsToViewerAxes(settings: {
  z_up?: boolean | undefined;
}): boolean {
  return settings.z_up !== true;
}

/** The settings that a given state of the orientation control asks for. */
export function withViewerAxisOrientation(
  settings: VisualizationAssetSettings,
  orientToViewerAxes: boolean,
): VisualizationAssetSettings {
  return { ...settings, z_up: !orientToViewerAxes };
}
